/**
 * Where "previous" and "next" go, as pure functions over one ordered list.
 *
 * The ordered list is the work queue's current page (`queue.items`). That is deliberate and it
 * is the whole point of this module: the record sidebar renders that array, the pager steps
 * through that array, and complete-and-advance walks that array. A pager whose order disagrees
 * with the list the annotator is looking at is worse than no pager, because it teaches them to
 * distrust the position readout — so there is exactly one ordering and it lives here.
 *
 * Two levels, not one
 * -------------------
 * The queue is a list of **inscriptions**; the workspace edits an **image**, and a record can
 * carry several photographs of the same stone. So a step first moves within the open record's
 * images and only then to the adjacent record. That mirrors what `nextImageId()` in the
 * workspace has always done (sibling image first, then the next record).
 *
 * Flattening the sidebar to a list of images is still not done, though the reason has narrowed.
 * A task queue row now carries `next_image_id` — the one image that task would open — so the
 * *first* step no longer needs a lookup. What it does not carry is the record's whole image
 * list, and a flat sidebar would need that for every row (fifty per page) before it could
 * render. Two levels stays.
 *
 * Dispatch vs. navigation
 * -----------------------
 * `stepFrom` is positional — pressing "previous" must be able to reach a record that is already
 * finished, because the reason for pressing it is usually to fix a box. `nextUnfinishedIndex`
 * is the dispatch rule used by complete-and-advance, which skips finished work. Same array,
 * same direction, different predicate, one place to read both.
 */

import type { ImageSummary, InscriptionSummary } from './api/types';

export type NavDirection = -1 | 1;

/** Which end of a record a step lands on. `first` going forward, `last` going back. */
export type NavEdge = 'first' | 'last';

export type NavStep =
  /** Another image of the record that is already open. */
  | { kind: 'image'; imageId: number; imageIndex: number }
  /** An adjacent record on the loaded queue page. */
  | { kind: 'record'; inscriptionId: number; recordIndex: number; edge: NavEdge }
  /** Off the end of the loaded page; the queue has more. */
  | { kind: 'page'; offset: number; edge: NavEdge };

export interface NavContext {
  /** The loaded queue page, in queue order. */
  records: readonly InscriptionSummary[];
  /** Position of the open record in `records`, or -1 when it is not on this page. */
  recordIndex: number;
  /** Images of the open record, in id order (the order the API returns). */
  images: readonly ImageSummary[];
  /** Position of the open image in `images`, or -1 when it is not known yet. */
  imageIndex: number;
  /** Offset of `records[0]` within the whole filtered queue. */
  offset: number;
  pageSize: number;
  /** How many inscriptions match the filter across all pages. */
  total: number;
  /** The queue page request is in flight. */
  loading: boolean;
}

export function emptyNavContext(): NavContext {
  return {
    records: [],
    recordIndex: -1,
    images: [],
    imageIndex: -1,
    offset: 0,
    pageSize: 0,
    total: 0,
    loading: false,
  };
}

/**
 * The step in `direction`, or null when there is nowhere to go.
 *
 * Null is never silent: `blockedReason` says why, and the caller renders it.
 */
export function stepFrom(context: NavContext, direction: NavDirection): NavStep | null {
  const { records, recordIndex, images, imageIndex, offset, pageSize, total } = context;

  if (imageIndex >= 0) {
    const nextImage = images[imageIndex + direction];
    if (nextImage) {
      return { kind: 'image', imageId: nextImage.id, imageIndex: imageIndex + direction };
    }
  }

  // Not on the loaded page: there is no defensible "next" from a position we do not have.
  // Guessing one (the first record, say) would make the pager disagree with the list.
  if (recordIndex < 0) return null;

  const edge: NavEdge = direction === 1 ? 'first' : 'last';
  const nextRecord = records[recordIndex + direction];
  if (nextRecord) {
    return {
      kind: 'record',
      inscriptionId: nextRecord.id,
      recordIndex: recordIndex + direction,
      edge,
    };
  }

  if (pageSize > 0) {
    if (direction === 1 && offset + records.length < total) {
      return { kind: 'page', offset: offset + pageSize, edge };
    }
    if (direction === -1 && offset > 0) {
      return { kind: 'page', offset: Math.max(0, offset - pageSize), edge };
    }
  }

  return null;
}

/**
 * Why `stepFrom` returned null, in the annotator's terms.
 *
 * Always a sentence, never an empty string: a disabled control with no explanation is the thing
 * this is here to avoid.
 */
export function blockedReason(context: NavContext, direction: NavDirection): string {
  const { records, recordIndex, loading, total } = context;
  const word = direction === 1 ? 'next' : 'previous';
  if (loading && records.length === 0) return 'The work queue is still loading.';
  if (records.length === 0) {
    return 'This task has nothing left in its queue, so there is nothing to step to.';
  }
  if (recordIndex < 0) {
    return (
      'This inscription is not in the work-queue list shown here — it was opened from the ' +
      'corpus browser, or the queue has moved on. Open it from the work queue to step ' +
      'through from this position.'
    );
  }
  const position = context.offset + recordIndex + 1;
  return direction === 1
    ? `This is the last inscription in the queue (${position} of ${total}), so there is no ${word} one.`
    : `This is the first inscription in the queue (${position} of ${total}), so there is no ${word} one.`;
}

/** One-line description of where a step lands, for a button title. */
export function describeStep(context: NavContext, step: NavStep, direction: NavDirection): string {
  const word = direction === 1 ? 'Next' : 'Previous';
  switch (step.kind) {
    case 'image':
      return `${word} image of this inscription (image ${step.imageIndex + 1} of ${context.images.length})`;
    case 'record': {
      const record = context.records[step.recordIndex];
      const at = context.offset + step.recordIndex + 1;
      return `${word} inscription: ${record?.siglum_raw ?? `#${step.inscriptionId}`} (${at} of ${context.total})`;
    }
    default:
      return `${word} page of the work queue, then its ${step.edge === 'first' ? 'first' : 'last'} inscription`;
  }
}

/** "12 of 60", or null when the open record is not on the loaded page. */
export function recordPosition(context: NavContext): { index: number; total: number } | null {
  if (context.recordIndex < 0) return null;
  return { index: context.offset + context.recordIndex + 1, total: context.total };
}

/** "image 2 of 3", or null when the record has at most one image. */
export function imagePosition(context: NavContext): { index: number; total: number } | null {
  if (context.imageIndex < 0 || context.images.length < 2) return null;
  return { index: context.imageIndex + 1, total: context.images.length };
}

/**
 * The dispatch rule behind complete-and-advance: the next record *with work left*.
 *
 * Kept here beside `stepFrom` so the difference between the two is visible in one file. The
 * pager is positional; this one skips records with nothing left, because the annotator asked
 * for "the next thing to do", not "the next row".
 *
 * "Nothing left" is the **task's** judgement, not this module's. An image with every letter
 * boxed is finished for the boxing task and untouched for the direction one, so the predicate
 * is a parameter — the work queue passes `pending_images > 0`. The default is boxing's, for
 * callers stepping through plain `InscriptionSummary` rows.
 */
export function nextUnfinishedIndex<T extends InscriptionSummary>(
  records: readonly T[],
  fromIndex: number,
  hasWorkLeft: (record: T) => boolean = (record) => record.image_count > record.images_done,
): number {
  if (fromIndex < 0) return -1;
  for (let i = fromIndex + 1; i < records.length; i += 1) {
    const candidate = records[i];
    if (candidate && hasWorkLeft(candidate)) return i;
  }
  return -1;
}

export type RecordProgressState = 'done' | 'in-progress' | 'not-started' | 'no-imagery';

/**
 * How far along a record is, as a state with a **word**.
 *
 * Colour is never the only signal, here as much as on a box: the sidebar renders this word and
 * a shape, and uses hue only as reinforcement.
 */
export function recordProgressState(record: InscriptionSummary): RecordProgressState {
  if (record.image_count === 0) return 'no-imagery';
  if (record.images_done >= record.image_count) return 'done';
  if (record.images_done > 0 || record.annotation_count > 0) return 'in-progress';
  return 'not-started';
}

export const RECORD_PROGRESS_LABEL: Record<RecordProgressState, string> = {
  done: 'done',
  'in-progress': 'in progress',
  'not-started': 'not started',
  'no-imagery': 'no imagery',
};

/** A shape, not a hue. Rendered `aria-hidden`; the label above carries the meaning. */
export const RECORD_PROGRESS_MARK: Record<RecordProgressState, string> = {
  done: '■',
  'in-progress': '◧',
  'not-started': '□',
  'no-imagery': '×',
};

/**
 * Boxes recorded against expected letters, for the row's progress bar.
 *
 * Deliberately **not** called "resolved": the queue summary counts boxes across the record's
 * images and knows nothing about letters marked not-present, so this is not the workspace's
 * resolved/expected figure and must not be labelled as if it were. It is a rough "how much has
 * been drawn here", which is what the row needs.
 */
export function recordBoxFraction(record: InscriptionSummary): number {
  if (record.token_count <= 0) return 0;
  return Math.min(1, record.annotation_count / record.token_count);
}

/**
 * The siglum as a narrow list row should show it.
 *
 * `siglum_raw` is what a scholar cites and is what the row must ultimately carry, but many
 * of them end in a bibliographic parenthetical — `AMJ 003 (Jobling 1982: 469 )`,
 * `Jobling 1982: 469; Jobling 1983: 189 )` — which is longer than the identifier itself and
 * pushes the identifying part out of a 264px rail.
 *
 * The cut is the same one `corpus/parser.py::normalize_siglum` makes when it derives the
 * storage key, so a row shows exactly the part that identifies the stone. It is **display
 * only**: nothing is stored, exported or joined on this, and the full raw value goes in the
 * row's `title` so the reference is one hover away and never lost.
 *
 * Deliberately not `siglum` (the normalized key): that carries underscores for spaces, and
 * `Al_Muqayil.A_Hism_1` is a storage artifact to read past, not a name.
 */
export function displaySiglum(record: InscriptionSummary): string {
  const raw = record.siglum_raw;
  const paren = raw.indexOf('(');
  const trimmed = paren === -1 ? raw : raw.slice(0, paren);
  // A title that is *only* a parenthetical would trim to nothing; keep the raw value then.
  return trimmed.trim() || raw;
}
